import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import digisakaLogo from '../../../assets/logo-transparent.png';
import { formatReportDateShort } from './cropCalendarReport';
import {
  buildFieldHealthViewModel,
  chartYTicks,
  clearPoints,
  extractSeries,
  formatCloudOutlierValue,
  formatFieldHealthValue,
  reportIndices,
  trendLabel,
  type FieldHealthReportInput,
  type TrendView,
} from './fieldHealthReportModel';

export type { FieldHealthReportInput } from './fieldHealthReportModel';

/** A4 at 96dpi */
const PAGE_W = 794;
const PAGE_H = 1123;
const SIDE_PAD = 57;

const FONT_LINK =
  'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap';

const FH_FOOTER_NOTE = 'This is a field estimate, not a guarantee.';

const CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  :root{
    --g:#0e5a2f; --g2:#126A37; --g3:#e7f1ea; --gold:#b7852b;
    --ink:#14201a; --mut:#667469; --line:#e4e8e2; --amb:#9a5b00; --ambs:#fff3dc;
  }
  body{
    margin:0; background:#fff; color:var(--ink);
    font:12.5px/1.5 Inter, system-ui, -apple-system, sans-serif;
    -webkit-print-color-adjust:exact; print-color-adjust:exact;
  }
  .paper{
    width:${PAGE_W}px; margin:0 auto; background:#fff;
    padding:0 ${SIDE_PAD}px 52px;
  }
  .serif{ font-family: Fraunces, Georgia, 'Times New Roman', serif; }
  .top{
    margin:0 -${SIDE_PAD}px; min-height:121px; padding:0 ${SIDE_PAD}px;
    background:linear-gradient(120deg,#0a4624,#126A37 60%,#1f8a4b);
    color:#fff; display:flex; align-items:center; justify-content:space-between;
    position:relative; overflow:hidden;
  }
  .top:after{
    content:""; position:absolute; right:-76px; top:-76px; width:265px; height:265px;
    border-radius:50%; border:1px solid rgba(255,255,255,.14);
    box-shadow:0 0 0 53px rgba(255,255,255,.04);
  }
  .brand{ display:flex; gap:10px; align-items:center; z-index:1; }
  .brand-logo{ height:38px; width:auto; object-fit:contain; display:block; flex-shrink:0; }
  .brand b{ font-size:19px; display:block; line-height:1.1; }
  .brand span{ font-size:10.5px; opacity:.8; letter-spacing:.14em; text-transform:uppercase; }
  .meta{ text-align:right; font-size:11px; z-index:1; line-height:1.45; }
  .hero{ margin:30px 0 15px; display:flex; justify-content:space-between; align-items:flex-end; gap:10px; }
  h1{ font-size:32px; line-height:1.05; margin:0; font-weight:700; }
  .fid{ color:var(--mut); margin-top:3px; font-size:12px; }
  .pill{
    background:var(--g3); color:var(--g); font-weight:700; font-size:11px;
    padding:5px 12px; border-radius:99px; white-space:nowrap;
  }
  .lbl{
    font-size:10.5px; letter-spacing:.14em; text-transform:uppercase; font-weight:700;
    color:var(--mut); margin:0 0 6px;
  }
  .card{
    border:1px solid var(--line); border-radius:14px; padding:19px 23px;
    margin-bottom:17px; background:#fff;
  }
  .verdict{
    background:linear-gradient(135deg,#f3faf5,#e3f1e8); border-color:#cfe3d6;
    display:grid; grid-template-columns:1.5fr 1fr; gap:30px; align-items:center;
  }
  .verdict h2{ font-size:27px; line-height:1.1; color:var(--g2); margin:2px 0 6px; font-weight:700; }
  .verdict p{ margin:0; color:#34443a; font-size:13px; line-height:1.45; }
  .kp{ display:grid; gap:8px; }
  .kp div{
    background:#fff; border-radius:10px; padding:7px 12px;
    display:flex; justify-content:space-between; align-items:baseline; border:1px solid #d9e8de;
  }
  .kp b{ font-size:18px; color:var(--g2); font-weight:700; }
  .kp span{ color:var(--mut); font-size:11.5px; }
  .ov{ display:grid; grid-template-columns:repeat(3,1fr); gap:15px; margin-bottom:17px; }
  .ov.cols-2{ grid-template-columns:repeat(2,1fr); }
  .ov .card{ margin:0; padding:15px 17px; }
  .code{
    font-size:10.5px; font-weight:700; letter-spacing:.06em; border:1px solid var(--line);
    border-radius:6px; padding:1px 6px; color:var(--mut);
  }
  .big{ font-size:30px; font-weight:700; line-height:1.1; margin:6px 0 2px; }
  .tr{
    display:inline-block; font-size:11px; font-weight:700; padding:2px 9px; border-radius:99px;
  }
  .tr.up{ background:var(--g3); color:var(--g); }
  .tr.steady{ background:#e8eef7; color:#1d5fa8; }
  .tr.down{ background:var(--ambs); color:var(--amb); }
  .info{ display:grid; grid-template-columns:repeat(4,1fr); gap:8px; }
  .info small{
    display:block; color:var(--mut); font-size:10px; letter-spacing:.08em; text-transform:uppercase;
  }
  .info b{ font-size:13px; font-weight:600; }
  h2.sec{ font-size:21px; margin:26px 0 4px; font-weight:700; }
  .sub{ color:var(--mut); margin:0 0 12px; font-size:12px; }
  .ih{ display:flex; justify-content:space-between; align-items:center; margin-bottom:2px; }
  .ih h3{ margin:0; font-size:18px; font-weight:700; }
  .st{
    display:grid; grid-template-columns:repeat(4,1fr); gap:6px; margin:6px 0 10px;
  }
  .st div{ background:#f5f7f4; border-radius:10px; padding:7px 10px; }
  .st small{
    display:block; color:var(--mut); font-size:10px; letter-spacing:.08em; text-transform:uppercase;
  }
  .st b{ font-size:18px; font-weight:700; }
  .mean{
    background:var(--g3); border-left:4px solid var(--g2); border-radius:8px;
    padding:9px 12px; color:#25382c; font-size:12px; line-height:1.5;
  }
  .warn{
    background:var(--ambs); color:var(--amb); border-radius:8px;
    padding:7px 12px; margin-top:8px; font-size:11.5px; line-height:1.45;
  }
  .gl{ columns:2; column-gap:30px; color:#34443a; font-size:12px; line-height:1.45; }
  .gl p{ margin:0 0 5px; break-inside:avoid; }
  .gl b{ color:var(--g2); }
  svg text{ font:10px Inter, sans-serif; fill:#667469; }
`;

function esc(value: string | number | null | undefined): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fhReportId(farmId: string, preparedIso: string): string {
  const day = preparedIso.replace(/-/g, '').slice(0, 8);
  return `FH-${farmId || 'X'}-${day}`;
}

function trendPillFromView(t: TrendView): string {
  return `<span class="tr ${t.cls}">${esc(t.arrow)} ${esc(trendLabel(t))}</span>`;
}

function sparklineSvg(
  points: ReturnType<typeof extractSeries>,
  color: string,
  outliers: Set<string>
): string {
  const clear = clearPoints(points, outliers);
  if (clear.length < 2) return '';
  const w = 120;
  const h = 32;
  const min = Math.min(...clear.map((p) => p.value));
  const max = Math.max(...clear.map((p) => p.value));
  const range = max - min || 1;
  const coords = clear
    .map((p, i) => {
      const x = 6 + (i / (clear.length - 1)) * (w - 12);
      const y = h - 6 - ((p.value - min) / range) * (h - 12);
      return `${x},${y}`;
    })
    .join(' ');
  return `<svg viewBox="0 0 ${w} ${h}" width="100%" style="margin:6px 0 4px"><polyline points="${coords}" fill="none" stroke="${color}" stroke-width="2" stroke-linejoin="round"/></svg>`;
}

function timeSeriesChartSvg(
  points: ReturnType<typeof extractSeries>,
  color: string,
  outliers: Set<string>,
  height = 210
): string {
  if (points.length < 1) return '';
  const left = 44;
  const right = 626;
  const plotTop = 16;
  const plotBottom = 176;
  const plotH = plotBottom - plotTop;
  const clear = clearPoints(points, outliers);
  const allValues = clear.length ? clear.map((p) => p.value) : points.map((p) => p.value);
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const pad = (max - min) * 0.08 || 0.05;
  const yMin = min - pad;
  const yMax = max + pad;
  const yRange = yMax - yMin || 1;
  const xFor = (i: number) => left + (i / Math.max(points.length - 1, 1)) * (right - left);
  const yFor = (v: number) => plotBottom - ((v - yMin) / yRange) * plotH;
  const clearLine = clear
    .map((p) => {
      const i = points.findIndex((pt) => pt.date === p.date);
      return `${xFor(i)},${yFor(p.value)}`;
    })
    .join(' ');
  const areaPath =
    clear.length >= 2
      ? `M${clear
          .map((p) => {
            const i = points.findIndex((pt) => pt.date === p.date);
            return `${xFor(i)},${yFor(p.value)}`;
          })
          .join(' L')} L${xFor(points.findIndex((pt) => pt.date === clear[clear.length - 1].date))},${plotBottom} L${xFor(points.findIndex((pt) => pt.date === clear[0].date))},${plotBottom}Z`
      : '';
  const grid = chartYTicks(yMin, yMax)
    .map((v) => {
      const y = yFor(v);
      return `<line x1="${left}" x2="${right}" y1="${y}" y2="${y}" stroke="#e4e8e2"/><text x="38" y="${y + 3}" text-anchor="end">${formatFieldHealthValue(v, v >= 10 ? 1 : 2)}</text>`;
    })
    .join('');
  const labelEvery = Math.max(1, Math.floor(points.length / 7));
  const pointMarkup = points
    .map((p, i) => {
      const x = xFor(i);
      const isOutlier = outliers.has(p.date);
      const y = isOutlier ? plotBottom : yFor(p.value);
      const showLabel = i % labelEvery === 0 || i === points.length - 1;
      const dateLabel = showLabel
        ? `<text x="${x}" y="198" text-anchor="middle">${esc(formatReportDateShort(p.date))}</text>`
        : '';
      if (isOutlier) {
        return `<circle cx="${x}" cy="${y}" r="5.5" fill="#fff" stroke="#c9822b" stroke-width="2.5" stroke-dasharray="3 2"/>
          <text x="${x + 9}" y="${y - 6}" style="fill:#9a5b00;font-weight:600">${formatCloudOutlierValue(p.value)} · cloud?</text>
          ${dateLabel}`;
      }
      const isLatest = i === points.length - 1;
      return `<circle cx="${x}" cy="${yFor(p.value)}" r="4" fill="#fff" stroke="${color}" stroke-width="2.4"/>
        ${dateLabel}
        ${isLatest ? `<circle cx="${x}" cy="${yFor(p.value)}" r="6.5" fill="${color}"/>` : ''}`;
    })
    .join('');
  return `<svg viewBox="0 0 640 ${height}" width="100%" role="img" aria-label="Reading history">
    ${grid}
    ${areaPath ? `<path d="${areaPath}" fill="${color}" opacity=".10"/>` : ''}
    ${clearLine ? `<polyline points="${clearLine}" fill="none" stroke="${color}" stroke-width="2.6" stroke-linejoin="round"/>` : ''}
    ${pointMarkup}
  </svg>`;
}

function weatherLineChartSvg(points: ReturnType<typeof extractSeries>, color: string): string {
  if (points.length < 2) return '';
  const left = 44;
  const right = 626;
  const plotTop = 12;
  const plotBottom = 160;
  const plotH = plotBottom - plotTop;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.08 || 1;
  const yMin = min - pad;
  const yMax = max + pad;
  const yRange = yMax - yMin || 1;
  const xFor = (i: number) => left + (i / (points.length - 1)) * (right - left);
  const yFor = (v: number) => plotBottom - ((v - yMin) / yRange) * plotH;
  const line = points.map((p, i) => `${xFor(i)},${yFor(p.value)}`).join(' ');
  const area = `M${line.replace(/ /g, ' L')} L${right},${plotBottom} L${left},${plotBottom}Z`;
  const grid = chartYTicks(yMin, yMax)
    .map((v) => {
      const y = yFor(v);
      return `<line x1="${left}" x2="${right}" y1="${y}" y2="${y}" stroke="#e4e8e2"/><text x="38" y="${y + 3}" text-anchor="end">${formatFieldHealthValue(v, 1)}</text>`;
    })
    .join('');
  const labels = [0, Math.floor(points.length / 6), Math.floor(points.length / 3), Math.floor(points.length / 2), Math.floor((points.length * 2) / 3), Math.floor((points.length * 5) / 6), points.length - 1]
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .map((i) => `<text x="${xFor(i)}" y="180" text-anchor="middle">${esc(formatReportDateShort(points[i].date))}</text>`)
    .join('');
  return `<svg viewBox="0 0 640 190" width="100%" role="img">${grid}<path d="${area}" fill="${color}" opacity=".12"/><polyline points="${line}" fill="none" stroke="${color}" stroke-width="2" stroke-linejoin="round"/>${labels}</svg>`;
}

function weatherBarChartSvg(points: ReturnType<typeof extractSeries>, color: string): string {
  if (!points.length) return '';
  const left = 44;
  const right = 626;
  const plotBottom = 160;
  const plotTop = 12;
  const plotH = plotBottom - plotTop;
  const max = Math.max(...points.map((p) => p.value), 1);
  const barW = Math.max(2, (right - left) / points.length - 1.5);
  const grid = chartYTicks(0, max)
    .map((v) => {
      const y = plotBottom - (v / max) * plotH;
      return `<line x1="${left}" x2="${right}" y1="${y}" y2="${y}" stroke="#e4e8e2"/><text x="38" y="${y + 3}" text-anchor="end">${formatFieldHealthValue(v, 0)}</text>`;
    })
    .join('');
  const bars = points
    .map((p, i) => {
      const x = left + i * ((right - left) / points.length) + 1;
      const h = (p.value / max) * plotH;
      const y = plotBottom - h;
      const opacity = p.value >= max * 0.85 ? 0.95 : 0.55;
      return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${barW.toFixed(1)}" height="${h.toFixed(1)}" rx="1.5" fill="${color}" opacity="${opacity}"/>`;
    })
    .join('');
  const labels = [0, Math.floor(points.length / 6), Math.floor(points.length / 3), Math.floor(points.length / 2), Math.floor((points.length * 2) / 3), Math.floor((points.length * 5) / 6), points.length - 1]
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .map((i) => {
      const x = left + i * ((right - left) / points.length) + barW / 2;
      return `<text x="${x.toFixed(0)}" y="180" text-anchor="middle">${esc(formatReportDateShort(points[i].date))}</text>`;
    })
    .join('');
  return `<svg viewBox="0 0 640 190" width="100%" role="img">${grid}${bars}${labels}</svg>`;
}

function buildReportHtml(report: FieldHealthReportInput, logoSrc: string): string {
  const vm = buildFieldHealthViewModel(report);
  const indices = reportIndices(report);
  const farmId = String(report.fieldInfo?.farm_id ?? '');
  const logo = logoSrc ? `<img class="brand-logo" src="${logoSrc}" alt="Digisaka" />` : '';
  const ovCols = indices.length === 2 ? ' cols-2' : '';

  const overview = vm.indices
    .map(
      (index) => `<div class="card">
    <div style="display:flex;justify-content:space-between;align-items:center">
      <b>${esc(index.label)}</b><span class="code">${esc(index.key.toUpperCase())}</span>
    </div>
    <div class="big serif" style="color:${index.color}">${formatFieldHealthValue(index.stats.latest)}</div>
    <div style="color:var(--mut);font-size:11px">${esc(index.latestDateLabel)}</div>
    ${sparklineSvg(index.points, index.color, new Set(index.outliers))}
    ${trendPillFromView(index.trend)}
  </div>`
    )
    .join('');

  const detailCards = vm.indices
    .map((index) => {
      const recBlock =
        index.interpretation || index.recommendation
          ? `<div class="mean"><b>What this means (${esc(index.latestDateLabel)}):</b> ${esc(index.interpretation || '')}<br><b>What to do:</b> ${esc(index.recommendation || '')}</div>`
          : '';
      const warnBlocks = index.outlierDateLabels
        .map((d) => `<div class="warn">Reading on ${esc(d)} looks cloud-affected and is not counted.</div>`)
        .join('');
      return `<div class="card">
    <div class="ih"><h3 class="serif">${esc(index.label)} <span class="code">${esc(index.key.toUpperCase())}</span></h3>${trendPillFromView(index.trend)}</div>
    <div style="color:var(--mut);margin-bottom:4px">${esc(index.subtitle)}</div>
    ${timeSeriesChartSvg(index.points, index.color, new Set(index.outliers))}
    <div class="st">
      <div><small>Latest</small><b style="color:${index.color}">${formatFieldHealthValue(index.stats.latest)}</b></div>
      <div><small>Average</small><b>${formatFieldHealthValue(index.stats.mean)}</b></div>
      <div><small>Highest</small><b>${formatFieldHealthValue(index.stats.max)}</b></div>
      <div><small>Lowest</small><b>${formatFieldHealthValue(index.stats.min)}</b></div>
    </div>
    ${recBlock}
    ${warnBlocks}
  </div>`;
    })
    .join('');

  let weatherHtml = '';
  if (vm.temperature || vm.rainfall) {
    weatherHtml = `<h2 class="sec serif">Weather in the same period</h2>
    <p class="sub">Daily values, ${vm.weatherDayCount} days.</p>`;
    if (vm.temperature) {
      weatherHtml += `<div class="card">
      <div class="ih"><h3 class="serif">Temperature (°C)</h3></div>
      ${weatherLineChartSvg(vm.temperature.points, '#c9552b')}
      <div class="st" style="grid-template-columns:repeat(3,1fr)">
        <div><small>Average</small><b style="color:#c9552b">${formatFieldHealthValue(vm.temperature.stats.mean, 1)} °C</b></div>
        <div><small>Hottest day</small><b>${formatFieldHealthValue(vm.temperature.stats.max, 1)} °C</b></div>
        <div><small>Coolest day</small><b>${formatFieldHealthValue(vm.temperature.stats.min, 1)} °C</b></div>
      </div>
    </div>`;
    }
    if (vm.rainfall) {
      weatherHtml += `<div class="card">
      <div class="ih"><h3 class="serif">Rainfall (mm)</h3></div>
      ${weatherBarChartSvg(vm.rainfall.points, '#1d78c9')}
      <div class="st">
        <div><small>Total</small><b style="color:#1d78c9">${formatFieldHealthValue(vm.rainfall.total ?? 0, 0)} mm</b></div>
        <div><small>Average / day</small><b>${formatFieldHealthValue((vm.rainfall.total ?? 0) / vm.rainfall.points.length, 1)}</b></div>
        <div><small>Wettest day</small><b>${formatFieldHealthValue(vm.rainfall.wettest?.value ?? 0, 1)} mm</b><span style="font-size:10.5px;color:var(--mut);display:block">${esc(vm.rainfall.wettest?.dateLabel || '')}</span></div>
        <div><small>Days over 10 mm</small><b>${vm.rainfall.wetDays ?? 0}</b></div>
      </div>
    </div>`;
    }
  }

  const cloudWarn =
    vm.cloudOutlierCount > 0
      ? `<div class="warn">⚠ ${vm.cloudOutlierCount} reading${vm.cloudOutlierCount === 1 ? '' : 's'} dropped sharply and may be hidden by cloud or haze. They are shown as dashed circles and left out of averages and trends.</div>`
      : '';

  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">
<link href="${FONT_LINK}" rel="stylesheet">
<style>${CSS}</style></head><body>
<div class="paper">
  <div class="top">
    <div class="brand">${logo}<div><b class="serif">Digisaka</b><span>Field health report</span></div></div>
    <div class="meta">Prepared <b>${esc(vm.preparedDate)}</b><br>${esc(vm.preparedTime)} · Philippines time</div>
  </div>
  <div class="hero">
    <div>
      <h1 class="serif">${esc(vm.farmName)}</h1>
      <div class="fid">Field ID ${esc(vm.farmId)}${report.area_ha != null ? ` · ${esc(report.area_ha.toFixed(2))} hectares` : ''}</div>
    </div>
    <span class="pill">${esc(vm.periodRange)} · ${vm.dayCount} days</span>
  </div>
  <div class="card verdict">
    <div>
      <p class="lbl">Your field in 10 seconds</p>
      <h2 class="serif">${esc(vm.verdictHeadline)}</h2>
      <p>${esc(vm.verdictSummary)}</p>
    </div>
    <div class="kp">
      <div><span>Satellite readings</span><b>${vm.obsCount}</b></div>
      <div><span>Days covered</span><b>${vm.dayCount}</b></div>
      <div><span>Measures checked</span><b>${vm.indices.length}</b></div>
    </div>
  </div>
  <p class="lbl">Latest reading by measure</p>
  <div class="ov${ovCols}">${overview}</div>
  <div class="card">
    <p class="lbl">Field information</p>
    <div class="info">
      <div><small>Farm</small><b>${esc(vm.farmName)}</b></div>
      <div><small>Field ID</small><b>${esc(vm.farmId)}</b></div>
      <div><small>Area</small><b>${esc(vm.areaLabel)}</b></div>
      <div><small>Crop</small><b>${esc(vm.cropLabel)}</b></div>
      <div style="grid-column:span 2"><small>Period</small><b>${esc(vm.periodRange)}</b></div>
      <div style="grid-column:span 2"><small>Measures</small><b>${esc(vm.measuresLabel)}</b></div>
    </div>
  </div>
  ${cloudWarn}
  <h2 class="sec serif">Crop health over time</h2>
  <p class="sub">Each chart shows one reading per clear satellite view.</p>
  ${detailCards}
  ${weatherHtml}
  <h2 class="sec serif">How to read this report</h2>
  <div class="card gl">
    <p><b>Greenness (NDVI, EVI, MSAVI2)</b> — higher means more healthy green cover. They differ in how they handle thick crops and bare soil.</p>
    <p><b>Dashed circle</b> — a reading that dropped sharply, usually from cloud or haze. It is not counted.</p>
    <p><b>Trend</b> — compares the first and latest clear readings. It is a guide, not a forecast.</p>
    <p><b>Weather</b> — daily temperature and rainfall for the field area over the same dates.</p>
  </div>
</div>
</body></html>`;
}

async function loadLogoDataUrl(): Promise<string> {
  try {
    const res = await fetch(digisakaLogo);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('logo read failed'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return '';
  }
}

async function waitForFonts(): Promise<void> {
  try {
    if (document.fonts?.load) {
      await Promise.all([
        document.fonts.load('700 32px Fraunces'),
        document.fonts.load('700 21px Fraunces'),
        document.fonts.load('400 12.5px Inter'),
        document.fonts.load('600 11px Inter'),
      ]);
    }
    await document.fonts?.ready;
  } catch {
    /* ignore */
  }
}

async function waitForImages(root: HTMLElement): Promise<void> {
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
    )
  );
}

function addPdfFooters(pdf: jsPDF, reportId: string, pageCount: number): void {
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i += 1) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(102, 116, 105);
    pdf.text(FH_FOOTER_NOTE, 15, h - 8);
    pdf.text(`Digisaka: Spatial Intelligence Platform · ${reportId}`, 15, h - 14);
    pdf.text(`Page ${i} of ${pageCount}`, w - 15, h - 8, { align: 'right' });
  }
}

export async function downloadFieldHealthPdf(report: FieldHealthReportInput): Promise<void> {
  const logoSrc = await loadLogoDataUrl();
  const farmId = String(report.fieldInfo?.farm_id ?? '');
  const reportId = fhReportId(farmId, report.analysisDate.slice(0, 10));

  const host = document.createElement('div');
  host.style.cssText = `position:fixed;left:-12000px;top:0;width:${PAGE_W}px;background:#fff;z-index:-1;`;
  host.innerHTML = buildReportHtml(report, logoSrc);
  document.body.appendChild(host);

  try {
    await waitForFonts();
    await waitForImages(host);
    await new Promise((r) => setTimeout(r, 500));

    const paper = host.querySelector('.paper') as HTMLElement;
    const fullHeight = paper.scrollHeight;
    const scale = 2;
    const canvas = await html2canvas(paper, {
      scale,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
      width: PAGE_W,
      height: fullHeight,
      windowWidth: PAGE_W,
      windowHeight: fullHeight,
    });

    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
    const imgWidthMm = 210;
    const pageHeightPx = PAGE_H * scale;
    const totalPages = Math.max(1, Math.ceil(canvas.height / pageHeightPx));

    for (let page = 0; page < totalPages; page += 1) {
      const sliceTop = page * pageHeightPx;
      const sliceHeight = Math.min(pageHeightPx, canvas.height - sliceTop);
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceHeight;
      const ctx = pageCanvas.getContext('2d');
      if (!ctx) continue;
      ctx.drawImage(canvas, 0, sliceTop, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
      const imgHeightMm = (sliceHeight * imgWidthMm) / canvas.width;
      if (page > 0) pdf.addPage();
      pdf.addImage(pageCanvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidthMm, imgHeightMm);
    }

    addPdfFooters(pdf, reportId, totalPages);

    const safeName = (report.fieldInfo?.farm_name || 'field').replace(/[^\w\-]+/g, '_').slice(0, 40);
    const dateStamp = new Date().toISOString().split('T')[0];
    pdf.save(`${safeName}_field_health_${dateStamp}.pdf`);
  } finally {
    host.remove();
  }
}
